<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Announcements & Updates</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><router-link to="/employee-dashboard">Dashboard</router-link></li>
                    <li class="breadcrumb-item active">Announcements</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Quick Stats -->
          <div class="row mb-4">
            <div class="col-md-3">
              <div class="card card-animate">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1">
                      <p class="text-muted mb-2">Total Announcements</p>
                      <h3 class="mb-0">{{ statistics.total }}</h3>
                    </div>
                    <div class="flex-shrink-0">
                      <div class="avatar-sm rounded-circle bg-primary mini-stat-icon">
                        <span class="avatar-title rounded-circle bg-primary">
                          <i class="bx bx-bullhorn font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-3">
              <div class="card card-animate">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1">
                      <p class="text-muted mb-2">Unread</p>
                      <h3 class="mb-0 text-danger">{{ statistics.unread }}</h3>
                    </div>
                    <div class="flex-shrink-0">
                      <div class="avatar-sm rounded-circle bg-danger mini-stat-icon">
                        <span class="avatar-title rounded-circle bg-danger">
                          <i class="bx bx-envelope font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-3">
              <div class="card card-animate">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1">
                      <p class="text-muted mb-2">This Week</p>
                      <h3 class="mb-0 text-info">{{ statistics.thisWeek }}</h3>
                    </div>
                    <div class="flex-shrink-0">
                      <div class="avatar-sm rounded-circle bg-info mini-stat-icon">
                        <span class="avatar-title rounded-circle bg-info">
                          <i class="bx bx-calendar font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-3">
              <div class="card card-animate">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1">
                      <p class="text-muted mb-2">Important</p>
                      <h3 class="mb-0 text-warning">{{ statistics.important }}</h3>
                    </div>
                    <div class="flex-shrink-0">
                      <div class="avatar-sm rounded-circle bg-warning mini-stat-icon">
                        <span class="avatar-title rounded-circle bg-warning">
                          <i class="bx bx-error font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Filters -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <div class="row align-items-end">
                    <div class="col-md-3">
                      <label class="form-label">Filter by Category</label>
                      <select v-model="filters.category" @change="loadAnnouncements" class="form-select">
                        <option value="">All Categories</option>
                        <option value="General">General</option>
                        <option value="HR">HR Updates</option>
                        <option value="IT">IT/Technical</option>
                        <option value="Events">Events</option>
                        <option value="Policy">Policy Changes</option>
                        <option value="Training">Training</option>
                        <option value="Safety">Safety & Security</option>
                      </select>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Priority</label>
                      <select v-model="filters.priority" @change="loadAnnouncements" class="form-select">
                        <option value="">All Priorities</option>
                        <option value="Critical">Critical</option>
                        <option value="High">High</option>
                        <option value="Normal">Normal</option>
                        <option value="Low">Low</option>
                      </select>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Status</label>
                      <select v-model="filters.status" @change="loadAnnouncements" class="form-select">
                        <option value="">All</option>
                        <option value="unread">Unread Only</option>
                        <option value="read">Read</option>
                      </select>
                    </div>
                    <div class="col-md-3">
                      <button @click="markAllAsRead" class="btn btn-outline-primary w-100" :disabled="statistics.unread === 0">
                        <i class="bx bx-check-double me-1"></i>
                        Mark All as Read
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading announcements...</p>
          </div>

          <!-- Announcements List -->
          <div v-else>
            <div class="row">
              <div class="col-12">

                <!-- No Announcements -->
                <div v-if="filteredAnnouncements.length === 0" class="text-center py-5">
                  <div class="avatar-lg mx-auto mb-4">
                    <div class="avatar-title bg-primary-subtle text-primary rounded-circle">
                      <i class="bx bx-info-circle" style="font-size: 3rem;"></i>
                    </div>
                  </div>
                  <h5>No Announcements Found</h5>
                  <p class="text-muted">There are no announcements matching your filters.</p>
                </div>

                <!-- Announcements Cards -->
                <div v-for="announcement in filteredAnnouncements" :key="announcement.id" class="card mb-3 announcement-card"
                     :class="{ 'unread': !announcement.isRead }">
                  <div class="card-body">
                    <div class="d-flex">
                      <!-- Icon/Category Badge -->
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-md rounded" :class="getCategoryBadgeClass(announcement.category)">
                          <span class="avatar-title rounded">
                            <i :class="getCategoryIcon(announcement.category)" style="font-size: 1.5rem;"></i>
                          </span>
                        </div>
                      </div>

                      <!-- Content -->
                      <div class="flex-grow-1">
                        <div class="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <h5 class="mb-1">
                              {{ announcement.title }}
                              <span v-if="!announcement.isRead" class="badge bg-danger ms-2">New</span>
                              <span v-if="announcement.priority === 'Critical'" class="badge bg-danger ms-2">
                                <i class="bx bx-error-circle me-1"></i>Critical
                              </span>
                              <span v-else-if="announcement.priority === 'High'" class="badge bg-warning ms-2">
                                <i class="bx bx-error me-1"></i>High Priority
                              </span>
                            </h5>
                            <div class="text-muted small">
                              <span class="badge bg-secondary-subtle text-secondary me-2">
                                {{ announcement.category }}
                              </span>
                              <i class="bx bx-calendar me-1"></i>
                              {{ formatDate(announcement.publishDate) }}
                              <span class="mx-2">•</span>
                              <i class="bx bx-user me-1"></i>
                              {{ announcement.postedBy }}
                            </div>
                          </div>
                          <div class="text-end">
                            <button @click="toggleRead(announcement)"
                                    class="btn btn-sm me-2"
                                    :class="announcement.isRead ? 'btn-outline-secondary' : 'btn-outline-primary'"
                                    :title="announcement.isRead ? 'Mark as Unread' : 'Mark as Read'">
                              <i class="bx" :class="announcement.isRead ? 'bx-envelope-open' : 'bx-envelope'"></i>
                            </button>
                            <button @click="viewAnnouncement(announcement)" class="btn btn-sm btn-primary">
                              <i class="bx bx-show me-1"></i> View
                            </button>
                          </div>
                        </div>

                        <!-- Short Description -->
                        <p class="mb-2 text-muted" v-if="announcement.shortDescription">
                          {{ announcement.shortDescription }}
                        </p>

                        <!-- Expandable Content -->
                        <div v-if="expandedAnnouncements.includes(announcement.id)">
                          <hr class="my-3">
                          <div class="announcement-content" v-html="announcement.content"></div>

                          <!-- Attachments -->
                          <div v-if="announcement.attachments && announcement.attachments.length > 0" class="mt-3">
                            <h6 class="mb-2">
                              <i class="bx bx-paperclip me-1"></i>Attachments
                            </h6>
                            <div class="d-flex flex-wrap gap-2">
                              <a v-for="(attachment, index) in announcement.attachments" :key="index"
                                 :href="getAttachmentUrl(attachment)" target="_blank"
                                 class="btn btn-sm btn-outline-secondary">
                                <i class="bx bx-file me-1"></i>
                                {{ attachment.name }}
                              </a>
                            </div>
                          </div>

                          <!-- Action Button -->
                          <div v-if="announcement.actionLink" class="mt-3">
                            <a :href="announcement.actionLink" class="btn btn-success">
                              <i class="bx bx-link-external me-1"></i>
                              {{ announcement.actionText || 'Take Action' }}
                            </a>
                          </div>
                        </div>

                        <!-- Expand/Collapse Button -->
                        <button @click="toggleExpand(announcement.id)" class="btn btn-sm btn-link text-decoration-none p-0 mt-2">
                          <span v-if="!expandedAnnouncements.includes(announcement.id)">
                            <i class="bx bx-chevron-down me-1"></i>Read More
                          </span>
                          <span v-else>
                            <i class="bx bx-chevron-up me-1"></i>Show Less
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Footer with reactions/acknowledgment -->
                  <div class="card-footer bg-light border-top" v-if="announcement.requiresAcknowledgment || announcement.reactions">
                    <div class="d-flex justify-content-between align-items-center">
                      <!-- Acknowledgment -->
                      <div v-if="announcement.requiresAcknowledgment">
                        <button v-if="!announcement.acknowledged"
                                @click="acknowledgeAnnouncement(announcement.id)"
                                class="btn btn-sm btn-primary">
                          <i class="bx bx-check me-1"></i>
                          Acknowledge Receipt
                        </button>
                        <span v-else class="badge bg-success">
                          <i class="bx bx-check-circle me-1"></i>Acknowledged
                        </span>
                      </div>

                      <!-- Reactions/Stats -->
                      <div class="text-muted small">
                        <i class="bx bx-show me-1"></i>
                        {{ announcement.views || 0 }} views
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            <!-- Pagination -->
            <div class="row" v-if="totalPages > 1">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
                        {{ Math.min(currentPage * itemsPerPage, totalAnnouncements) }} of
                        {{ totalAnnouncements }} announcements
                      </div>
                      <nav>
                        <ul class="pagination mb-0">
                          <li class="page-item" :class="{ disabled: currentPage === 1 }">
                            <a class="page-link" @click.prevent="changePage(currentPage - 1)" href="#">
                              <i class="bx bx-chevron-left"></i>
                            </a>
                          </li>
                          <li v-for="page in visiblePages" :key="page"
                              class="page-item" :class="{ active: currentPage === page }">
                            <a class="page-link" @click.prevent="changePage(page)" href="#">{{ page }}</a>
                          </li>
                          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                            <a class="page-link" @click.prevent="changePage(currentPage + 1)" href="#">
                              <i class="bx bx-chevron-right"></i>
                            </a>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
        <!-- end container-fluid -->
      </div>
      <!-- End Page-content -->
    </div>
    <!-- end content-minimize -->
  </div>
  <!-- end layout-wrapper -->
</template>

<script>
import { authUrl, execute, uploads } from "@/api";
import NavigationBar from "@/components/Navigation";

export default {
  name: "EmployeeAnnouncements",

  components: {
    NavigationBar
  },

  data() {
    return {
      loading: false,

      filters: {
        category: '',
        priority: '',
        status: ''
      },

      statistics: {
        total: 0,
        unread: 0,
        thisWeek: 0,
        important: 0
      },

      announcements: [],
      expandedAnnouncements: [],

      currentPage: 1,
      itemsPerPage: 10,
      totalAnnouncements: 0
    };
  },

  computed: {
    filteredAnnouncements() {
      let filtered = [...this.announcements];

      // Filter by read/unread status
      if (this.filters.status === 'unread') {
        filtered = filtered.filter(a => !a.isRead);
      } else if (this.filters.status === 'read') {
        filtered = filtered.filter(a => a.isRead);
      }

      return filtered;
    },

    totalPages() {
      return Math.ceil(this.totalAnnouncements / this.itemsPerPage);
    },

    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    }
  },

  mounted() {
    this.loadAnnouncements();
  },

  methods: {
    async loadAnnouncements() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getEmployeeAnnouncements");
        data.append("category", this.filters.category);
        data.append("priority", this.filters.priority);
        data.append("page", this.currentPage);
        data.append("itemsPerPage", this.itemsPerPage);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.announcements = res.data.data.announcements;
          this.statistics = res.data.data.statistics;
          this.totalAnnouncements = res.data.data.total;
        }
      } catch (error) {
        console.error("Failed to load announcements:", error);
        this.$toast?.error("Error loading announcements") || alert("Error loading announcements");
      } finally {
        this.loading = false;
      }
    },

    toggleExpand(announcementId) {
      const index = this.expandedAnnouncements.indexOf(announcementId);
      if (index > -1) {
        this.expandedAnnouncements.splice(index, 1);
      } else {
        this.expandedAnnouncements.push(announcementId);
        // Mark as read when expanded
        this.markAsRead(announcementId);
      }
    },

    async toggleRead(announcement) {
      try {
        const data = new FormData();
        data.append("function", "toggleAnnouncementRead");
        data.append("announcementId", announcement.id);
        data.append("isRead", announcement.isRead ? 0 : 1); // Send opposite value as integer

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          announcement.isRead = !announcement.isRead;

          // Update statistics
          if (announcement.isRead) {
            this.statistics.unread--;
          } else {
            this.statistics.unread++;
          }
        }
      } catch (error) {
        console.error("Failed to toggle read status:", error);
      }
    },

    async markAsRead(announcementId) {
      const announcement = this.announcements.find(a => a.id === announcementId);
      if (announcement && !announcement.isRead) {
        try {
          const data = new FormData();
          data.append("function", "markAnnouncementAsRead");
          data.append("announcementId", announcementId);

          const res = await execute(data, authUrl);

          if (res && res.data && res.data.success) {
            announcement.isRead = true;
            this.statistics.unread--;
          }
        } catch (error) {
          console.error("Failed to mark as read:", error);
        }
      }
    },

    async markAllAsRead() {
      try {
        const data = new FormData();
        data.append("function", "markAllAnnouncementsAsRead");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.announcements.forEach(a => a.isRead = true);
          this.statistics.unread = 0;
          this.$toast?.success("All announcements marked as read") || alert("All marked as read");
        }
      } catch (error) {
        console.error("Failed to mark all as read:", error);
        this.$toast?.error("Error marking all as read") || alert("Error marking all as read");
      }
    },

    async acknowledgeAnnouncement(announcementId) {
      try {
        const data = new FormData();
        data.append("function", "acknowledgeAnnouncement");
        data.append("announcementId", announcementId);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          const announcement = this.announcements.find(a => a.id === announcementId);
          if (announcement) {
            announcement.acknowledged = true;
          }
          this.$toast?.success("Announcement acknowledged") || alert("Acknowledged!");
        }
      } catch (error) {
        console.error("Failed to acknowledge:", error);
        this.$toast?.error("Error acknowledging announcement") || alert("Error");
      }
    },

    viewAnnouncement(announcement) {
      if (!this.expandedAnnouncements.includes(announcement.id)) {
        this.toggleExpand(announcement.id);
      }

      // Scroll to announcement
      this.$nextTick(() => {
        const element = document.getElementById(`announcement-${announcement.id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.loadAnnouncements();
        window.scrollTo(0, 0);
      }
    },

    getCategoryBadgeClass(category) {
      const classes = {
        'General': 'bg-primary',
        'HR': 'bg-info',
        'IT': 'bg-secondary',
        'Events': 'bg-success',
        'Policy': 'bg-warning',
        'Training': 'bg-info',
        'Safety': 'bg-danger'
      };
      return classes[category] || 'bg-primary';
    },

    getCategoryIcon(category) {
      const icons = {
        'General': 'bx bx-info-circle',
        'HR': 'bx bx-user',
        'IT': 'bx bx-laptop',
        'Events': 'bx bx-calendar-event',
        'Policy': 'bx bx-file',
        'Training': 'bx bx-book',
        'Safety': 'bx bx-shield'
      };
      return icons[category] || 'bx bx-bullhorn';
    },

    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      const now = new Date();
      const diffTime = Math.abs(now - d);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;

      return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    getAttachmentUrl(attachment) {
      // The attachment path is stored like: uploads/announcements/filename.pdf
      // We need to prepend the uploads base URL
      if (attachment.path) {
        return uploads + attachment.path;
      } else if (attachment.url) {
        return attachment.url;
      }
      return '#';
    }
  }
};
</script>

<style scoped>
.card-animate {
  transition: all 0.3s ease;
}

.card-animate:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.avatar-sm {
  height: 3rem;
  width: 3rem;
}

.avatar-md {
  height: 3.5rem;
  width: 3.5rem;
}

.avatar-lg {
  height: 5rem;
  width: 5rem;
}

.avatar-title {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
  color: white;
}

.mini-stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.announcement-card {
  transition: all 0.2s ease;
}

.announcement-card.unread {
  border-left: 4px solid #dc3545;
  background-color: #fff8f8;
}

.announcement-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.announcement-content {
  line-height: 1.8;
}

.announcement-content >>> p {
  margin-bottom: 1rem;
}

.announcement-content >>> ul,
.announcement-content >>> ol {
  margin-bottom: 1rem;
  padding-left: 2rem;
}

.badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.pagination .page-link {
  cursor: pointer;
}
</style>